import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { restaurantAPI, orderAPI } from '../../services/api';
import socketService from '../../services/socket';
import { useAuth } from '../../context/AuthContext';
import { claimAutoPrint } from '../../utils/printDedup';
import { applyAdminPrintSettings } from '../../utils/adminPrintSettings';

// Flutter App Theme Colors (exact match)
const theme = {
  primaryColor: '#FF6961',
  primaryLight: '#FF8A84',
  primaryDark: '#E6524A',
  secondaryColor: '#6366F1',
  backgroundColor: '#F8FAFC',
  surfaceColor: '#FFFFFF',
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  textHint: '#94A3B8',
  successColor: '#22C55E',
  warningColor: '#F59E0B',
  errorColor: '#EF4444',
  infoColor: '#3B82F6',
  radiusMedium: '12px',
};

// Toast notification types
const TOAST_TYPES = {
  SUCCESS: 'success',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error'
};

function RestaurantDashboard() {
  const navigate = useNavigate();
  const { restaurant: authRestaurant } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [stats, setStats] = useState(null);
  const [allOrders, setAllOrders] = useState([]);
  const [activeOrderTab, setActiveOrderTab] = useState('pending'); // 'pending', 'active', 'completed'
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(true);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [pricingEdge, setPricingEdge] = useState(null);
  const sliderRef = useRef(null);
  const ordersRef = useRef([]); // Ref for auto-print socket handler
  const printOrderRef = useRef(null); // Ref to printOrder function
  const toastIdRef = useRef(0);
  const fetchAllOrdersRef = useRef(null); // Ref to avoid stale closure in socket callbacks
  const fetchStatsRef = useRef(null); // Ref to avoid stale closure in socket callbacks
  const [toasts, setToasts] = useState([]); // Toast notification queue

  // Add toast notification
  const addToast = useCallback((message, type = TOAST_TYPES.INFO, duration = 5000) => {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  // Remove toast
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Auto-accept orders state
  const [autoAcceptOrders, setAutoAcceptOrders] = useState(true);
  const soundLoopRef = useRef(null);
  const loopAudioRef = useRef(null);

  // Opening hours state (matching Flutter app)
  const [isClosedBySchedule, setIsClosedBySchedule] = useState(false);
  const [scheduleMessage, setScheduleMessage] = useState('');
  const [showCannotOpenModal, setShowCannotOpenModal] = useState(false);

  // Helper: Get day name in lowercase
  const getDayName = () => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[new Date().getDay()];
  };

  // Helper: Parse time string "HH:MM" to minutes since midnight
  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return null;
    const parts = timeStr.split(':');
    if (parts.length !== 2) return null;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (isNaN(hours) || isNaN(minutes)) return null;
    return hours * 60 + minutes;
  };

  // Helper: Format time for display
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Check if current time is within opening hours (matching Flutter logic)
  const isWithinOpeningHours = (restaurantData) => {
    if (!restaurantData?.openingHours) return true;

    const dayName = getDayName();
    const dayHours = restaurantData.openingHours[dayName];

    if (!dayHours || dayHours.isOff) return false;

    const openMinutes = parseTimeToMinutes(dayHours.open);
    const closeMinutes = parseTimeToMinutes(dayHours.close);

    if (openMinutes === null || closeMinutes === null) return true;

    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    // Handle overnight hours (e.g., 21:00 - 02:00)
    if (closeMinutes <= openMinutes) {
      return currentMinutes >= openMinutes || currentMinutes < closeMinutes;
    }

    // Normal hours (e.g., 09:00 - 22:00)
    return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
  };

  // Check opening hours and update schedule message (matching Flutter logic)
  const checkOpeningHoursAndAutoToggle = (restaurantData) => {
    if (!restaurantData?.openingHours) {
      setIsClosedBySchedule(false);
      setScheduleMessage('');
      return;
    }

    const withinHours = isWithinOpeningHours(restaurantData);
    const dayName = getDayName();
    const dayHours = restaurantData.openingHours[dayName];

    if (!withinHours) {
      setIsClosedBySchedule(true);

      if (dayHours?.isOff) {
        setScheduleMessage('Closed today');
      } else if (dayHours?.open) {
        setScheduleMessage(`Opens at ${formatTime(dayHours.open)}`);
      } else {
        setScheduleMessage('Outside business hours');
      }
    } else {
      setIsClosedBySchedule(false);

      // Show closing time message when open
      if (dayHours?.close) {
        setScheduleMessage(`Open until ${formatTime(dayHours.close)}`);
      } else {
        setScheduleMessage('');
      }
    }
  };

  // Full refresh function - reloads all dashboard data
  const refreshDashboard = async () => {
    console.log('🔄 Dashboard: Refreshing all data...');
    try {
      await Promise.all([
        fetchRestaurant(),
        fetchStats(),
        fetchAllOrders()
      ]);
      console.log('✅ Dashboard: Refresh complete');
    } catch (error) {
      console.error('❌ Dashboard: Refresh failed:', error);
    }
  };

  useEffect(() => {
    checkSubscriptionStatus();

    // Auto-refresh when page becomes visible (user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('🔄 Dashboard: Page visible, auto-refreshing...');
        refreshDashboard();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Fetch the pricing edge (safe discount headroom + competitor markup) for the nudge card
  useEffect(() => {
    let alive = true;
    restaurantAPI.getPricingEdge()
      .then((res) => { if (alive && res?.data?.success) setPricingEdge(res.data.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Fetch auto-accept setting and listen for changes
  useEffect(() => {
    const fetchAutoAccept = async () => {
      try {
        const response = await restaurantAPI.getAutoAcceptSetting();
        if (response.data.success) {
          setAutoAcceptOrders(response.data.data.autoAcceptOrders ?? true);
        }
      } catch (err) {
        console.error('Failed to fetch auto-accept setting:', err);
      }
    };
    fetchAutoAccept();

    const handleAutoAcceptChanged = (data) => {
      if (data?.autoAcceptOrders !== undefined) {
        setAutoAcceptOrders(data.autoAcceptOrders);
      }
    };
    const unsubAutoAccept = socketService.on('auto_accept_changed', handleAutoAcceptChanged);

    return () => {
      unsubAutoAccept();
    };
  }, []);

  // FRONT-M2: depend on a derived boolean, not the whole `allOrders` array, so
  // the 2s alert interval isn't torn down + recreated (restarting the sound) on
  // every order update.
  const hasPendingOrders = useMemo(() => allOrders.some(o => o.status === 'pending'), [allOrders]);

  // Sound loop for pending orders
  useEffect(() => {
    if (hasPendingOrders) {
      // Start sound loop if not already running
      if (!soundLoopRef.current) {
        const playSound = () => {
          // Always use browser audio with R2 CDN URL (auto-updates without app rebuild)
          try {
            if (!loopAudioRef.current) {
              loopAudioRef.current = new Audio('https://pub-7ccc1a05d7f24ac88e57c3427b104297.r2.dev/sounds/restaurant/sounds-restaurant-1781034058176-2c7b89fc9d68f6a2.mp3');
            }
            loopAudioRef.current.volume = 1.0;
            loopAudioRef.current.currentTime = 0;
            loopAudioRef.current.play().catch(() => {});
          } catch (e) { /* ignore */ }
        };
        playSound(); // Play immediately
        soundLoopRef.current = setInterval(playSound, 2000); // Then every 2s
      }
    } else {
      // Stop sound loop
      if (soundLoopRef.current) {
        clearInterval(soundLoopRef.current);
        soundLoopRef.current = null;
      }
    }

    return () => {
      if (soundLoopRef.current) {
        clearInterval(soundLoopRef.current);
        soundLoopRef.current = null;
      }
    };
  }, [hasPendingOrders]);

  // Periodic opening hours check (every minute, matching Flutter app)
  useEffect(() => {
    if (!restaurant) return;

    // Check immediately
    checkOpeningHoursAndAutoToggle(restaurant);

    // Then check every minute
    const intervalId = setInterval(() => {
      checkOpeningHoursAndAutoToggle(restaurant);
    }, 60000); // 60 seconds

    return () => clearInterval(intervalId);
  }, [restaurant]);

  // Socket listener for real-time order updates and auto-print on auto-accept
  useEffect(() => {
    if (!authRestaurant?._id) return;

    // Connect to socket and join restaurant room
    socketService.connect();
    socketService.joinRestaurantRoom(authRestaurant._id);

    // Listen for order updates (status changes) - Use functional state update
    const unsubOrderUpdate = socketService.on('order_update', (data) => {
      console.log('📡 Dashboard: Order update received via socket:', data);

      // FUNCTIONAL STATE UPDATE: Update order status directly (official Socket.IO pattern)
      setAllOrders(prevOrders => {
        const updatedOrders = prevOrders.map(order => {
          if (order._id === data.orderId || order.orderNumber === data.orderNumber) {
            console.log('📡 Dashboard: Updating order status in state:', data.orderNumber, '->', data.status);
            // Auto-print when order transitions to 'preparing' (auto-accept)
            if (data.status === 'preparing' && data.previousStatus === 'pending') {
              console.log('🖨️ Dashboard: Auto-printing auto-accepted order:', data.orderNumber);
              // Show notification that order was auto-accepted
              const _ps = JSON.parse(localStorage.getItem('printerSettings') || '{}');
              addToast(`Order #${data.orderNumber} was auto-accepted!${_ps.printEnabled === 'false' ? '' : ' Printing...'}`, TOAST_TYPES.SUCCESS);
              if (printOrderRef.current) {
                // Use setTimeout to avoid state update during render
                // Pass isAutoAccept=true so receipt shows "Cashier: Autoaccept"
                setTimeout(() => printOrderRef.current(order, null, true), 500);
              }
            }
            return { ...order, status: data.status };
          }
          return order;
        });
        ordersRef.current = updatedOrders; // Keep ref in sync
        return updatedOrders;
      });
    });

    // Listen for new orders - Use functional state update
    const unsubNewOrder = socketService.on('new_order', (data) => {
      console.log('🆕 Dashboard: New order received via socket:', data);
      addToast(`New Order #${data.orderNumber} received!`, TOAST_TYPES.SUCCESS);

      // FUNCTIONAL STATE UPDATE: Add new order to state directly
      if (data.order) {
        setAllOrders(prevOrders => {
          const exists = prevOrders.some(o => o._id === data.order._id || o.orderNumber === data.order.orderNumber);
          if (exists) return prevOrders;
          const newOrders = [data.order, ...prevOrders];
          ordersRef.current = newOrders;
          return newOrders;
        });
      } else if (fetchAllOrdersRef.current) {
        fetchAllOrdersRef.current();
      }

      // Still fetch stats for updated counts
      if (fetchStatsRef.current) {
        fetchStatsRef.current();
      }
    });

    // Listen for order cancellations - Use functional state update
    const unsubOrderCancelled = socketService.on('order_cancelled', (data) => {
      console.log('❌ Dashboard: Order cancelled via socket:', data);
      addToast(`Order #${data.orderNumber} was cancelled${data.reason ? ': ' + data.reason : ''}`, TOAST_TYPES.WARNING);

      // FUNCTIONAL STATE UPDATE: Update order status to cancelled
      setAllOrders(prevOrders => {
        const updatedOrders = prevOrders.map(order => {
          if (order._id === data.orderId || order.orderNumber === data.orderNumber) {
            return { ...order, status: 'cancelled' };
          }
          return order;
        });
        ordersRef.current = updatedOrders;
        return updatedOrders;
      });

      if (fetchStatsRef.current) {
        fetchStatsRef.current();
      }
    });

    // Listen for order assignment (delivery partner assigned) - Use functional state update
    const unsubOrderAssigned = socketService.on('order_assigned', (data) => {
      console.log('🚴 Dashboard: Order assigned via socket:', data);
      addToast(`Order #${data.orderNumber}: Delivery partner ${data.deliveryPartnerName || ''} assigned`, TOAST_TYPES.SUCCESS);

      // FUNCTIONAL STATE UPDATE: Update order with assignment info
      setAllOrders(prevOrders => {
        const updatedOrders = prevOrders.map(order => {
          if (order._id === data.orderId || order.orderNumber === data.orderNumber) {
            return { ...order, status: data.status || 'assigned_delivery', deliveryPartnerId: data.deliveryPartnerId };
          }
          return order;
        });
        ordersRef.current = updatedOrders;
        return updatedOrders;
      });
    });

    // Listen for payment confirmation - Use functional state update
    const unsubPaymentConfirmed = socketService.on('payment_confirmed', (data) => {
      console.log('💳 Dashboard: Payment confirmed via socket:', data);
      addToast(`Order #${data.orderNumber}: Payment of ₹${data.total} confirmed`, TOAST_TYPES.SUCCESS);

      // FUNCTIONAL STATE UPDATE: Update order payment status and trigger auto-print
      setAllOrders(prevOrders => {
        const updatedOrders = prevOrders.map(order => {
          if (order._id === data.orderId || order.orderNumber === data.orderNumber) {
            // Auto-print for online payment orders
            if (printOrderRef.current) {
              console.log('🖨️ Dashboard: Auto-printing order after payment confirmation:', data.orderNumber);
              setTimeout(() => printOrderRef.current(order, null, false), 0);
            }
            return { ...order, paymentStatus: 'received' };
          }
          return order;
        });
        ordersRef.current = updatedOrders;
        return updatedOrders;
      });
    });

    // Listen for new reviews
    const unsubReviewReceived = socketService.on('review_received', (data) => {
      console.log('⭐ Dashboard: Review received:', data);
      const stars = data.restaurantRating ? '★'.repeat(Math.round(data.restaurantRating)) : '';
      addToast(`New Review ${stars}: ${data.customerName || 'Customer'} left a ${data.restaurantRating}/5 rating`, TOAST_TYPES.INFO, 8000);
    });

    // Listen for earnings update (after order delivered)
    const unsubEarningsUpdate = socketService.on('earnings_update', (data) => {
      console.log('💰 Dashboard: Earnings update:', data);
      addToast(`Order #${data.orderNumber} delivered! Earned ₹${data.netAmount?.toFixed(0) || data.grossAmount?.toFixed(0) || 0}`, TOAST_TYPES.SUCCESS);
      if (fetchStatsRef.current) {
        fetchStatsRef.current();
      }
    });

    // Listen for payout schedule notification
    const unsubPayoutSchedule = socketService.on('payout_schedule_notification', (data) => {
      console.log('📅 Dashboard: Payout schedule notification:', data);
      addToast(`Payout of ₹${data.amount?.toFixed(0) || 0} scheduled for ${data.scheduledDate ? new Date(data.scheduledDate).toLocaleDateString() : 'soon'}`, TOAST_TYPES.INFO, 10000);
    });

    // Listen for restaurant status change (sync across devices)
    const unsubStatusChanged = socketService.on('restaurant_status_changed', (data) => {
      console.log('🏪 Dashboard: Restaurant status changed:', data);
      if (data.source !== 'web') { // Only update if change came from elsewhere
        setIsOpen(data.isOpen);
        setSliderPosition(data.isOpen ? 0 : 1);
        addToast(`Restaurant is now ${data.isOpen ? 'OPEN' : 'CLOSED'}`, data.isOpen ? TOAST_TYPES.SUCCESS : TOAST_TYPES.WARNING);
      }
    });

    return () => {
      unsubOrderUpdate();
      unsubNewOrder();
      unsubOrderCancelled();
      unsubOrderAssigned();
      unsubPaymentConfirmed();
      unsubReviewReceived();
      unsubEarningsUpdate();
      unsubPayoutSchedule();
      unsubStatusChanged();
    };
  }, [authRestaurant?._id, addToast]);

  const checkSubscriptionStatus = async () => {
    try {
      console.log('🔍 Dashboard: Checking subscription status...');
      const response = await restaurantAPI.getSubscriptionPlanStatus();
      console.log('📡 Dashboard: Got subscription response:', response?.data);

      // Defensive check for response structure
      if (!response?.data?.data) {
        console.error('❌ Dashboard: Invalid subscription response structure');
        setLoading(false);
        return;
      }

      const data = response.data.data;

      // Commission-billing restaurants have NO subscription — skip the entire
      // subscription flow (no plan-selection redirect, no dashboard card).
      const isCommission = data.billingModel === 'commission';
      if (!isCommission) {
        // Only redirect a BRAND-NEW subscription restaurant (never subscribed) to
        // pick a plan. Returning subscribers just load the dashboard; order-taking
        // is enforced server-side by the subscription gate.
        const hasSubscriptionHistory = data.subscriptionPlan?.monthly?.isTrialUsed ||
                                       data.subscriptionPlan?.monthly?.paymentHistory?.length > 0 ||
                                       data.subscriptionPlan?.oneTimeFee?.initialPaymentStatus === 'paid';
        const noPlan = !data.subscriptionPlan ||
          data.subscriptionPlan.type === 'none' ||
          (data.subscriptionPlan.type === 'one_time' && data.subscriptionPlan.oneTimeFee?.initialPaymentStatus !== 'paid');
        if (noPlan && !hasSubscriptionHistory) {
          navigate('/restaurant/plan-selection');
          return;
        }
      }

      // NOTE: the trial/subscription dashboard CARD was removed for all restaurants;
      // subscription status/renewal lives on the Earnings & Subscription pages.
      await fetchRestaurant();
      await fetchStats();
      await fetchAllOrders();
    } catch (error) {
      console.error('❌ Dashboard: Failed to check subscription status:', error);
      console.error('❌ Dashboard: Error details:', error?.response?.data || error?.message);
      setLoading(false);
    }
  };

  const fetchRestaurant = async () => {
    try {
      console.log('🔍 Dashboard: Fetching restaurant...');
      const response = await restaurantAPI.getMyRestaurant();
      console.log('📡 Dashboard: Got restaurant response:', response?.data);

      if (response?.data?.data?.restaurant) {
        const restaurantData = response.data.data.restaurant;
        setRestaurant(restaurantData);
        setIsOpen(restaurantData.isOpen);
        // Sync slider position with backend state
        setSliderPosition(restaurantData.isOpen ? 0 : 1);
        // Check opening hours and update schedule message
        checkOpeningHoursAndAutoToggle(restaurantData);
        console.log('✅ Dashboard: Restaurant data set successfully, isOpen:', restaurantData.isOpen);
      } else {
        console.error('❌ Dashboard: Invalid restaurant response structure');
      }
    } catch (error) {
      console.error('❌ Dashboard: Failed to fetch restaurant:', error);
      console.error('❌ Dashboard: Restaurant error details:', error?.response?.data || error?.message);
    } finally {
      console.log('🏁 Dashboard: Setting loading to false');
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await restaurantAPI.getMyStats();
      setStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const fetchAllOrders = async () => {
    try {
      // Fetch all orders (like Flutter app)
      const response = await restaurantAPI.getOrders();
      const orders = response.data.data?.orders || [];
      setAllOrders(orders);
      ordersRef.current = orders; // Keep ref in sync for socket handlers
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  };

  // Keep refs updated with latest functions to avoid stale closures in socket callbacks
  useEffect(() => {
    fetchAllOrdersRef.current = fetchAllOrders;
    fetchStatsRef.current = fetchStats;
  });

  const toggleStatus = async () => {
    if (isTogglingStatus) return;

    const newIsOpen = !isOpen;

    // Block opening outside business hours (matching Flutter app)
    if (newIsOpen && isClosedBySchedule) {
      setShowCannotOpenModal(true);
      // Reset slider position
      setSliderPosition(isOpen ? 0 : 1);
      return;
    }

    setIsTogglingStatus(true);

    try {
      await restaurantAPI.updateStatus({ isOpen: newIsOpen });
      setIsOpen(newIsOpen);
      // Sync slider position with new state to prevent visual glitch
      setSliderPosition(newIsOpen ? 0 : 1);
      // Update schedule message after status change
      if (restaurant) {
        checkOpeningHoursAndAutoToggle(restaurant);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      // Reset slider position to original state on failure
      setSliderPosition(isOpen ? 0 : 1);
      alert('Failed to update status');
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const handleSliderDragStart = (e) => {
    if (isTogglingStatus) return;
    setIsDragging(true);
    setSliderPosition(isOpen ? 0 : 1);
  };

  const handleSliderDrag = (e) => {
    if (!isDragging || !sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const maxSlide = rect.width - 70;
    const newPosition = Math.max(0, Math.min(1, x / maxSlide));
    setSliderPosition(newPosition);
  };

  const handleSliderDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    // Toggle if dragged past threshold
    if (isOpen && sliderPosition > 0.7) {
      toggleStatus();
      // Don't reset position - toggleStatus will update it on success/failure
      return;
    } else if (!isOpen && sliderPosition < 0.3) {
      toggleStatus();
      // Don't reset position - toggleStatus will update it on success/failure
      return;
    }
    // Reset position only if threshold not met (no toggle triggered)
    setSliderPosition(isOpen ? 0 : 1);
  };

  const acceptOrder = async (orderId, orderToPrint = null) => {
    try {
      // Use 'preparing' status to accept order (matches Flutter app and backend)
      await restaurantAPI.updateOrderStatus(orderId, { status: 'preparing' });

      // Auto-print when order is accepted
      if (orderToPrint) {
        console.log('🖨️ Auto-printing order after acceptance:', orderToPrint.orderNumber);
        printOrder(orderToPrint);
      }

      fetchAllOrders();
      fetchStats();
    } catch (error) {
      console.error('Failed to accept order:', error);
      alert('Failed to accept order');
    }
  };

  const rejectOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to reject this order?')) return;
    try {
      await restaurantAPI.cancelOrder(orderId, 'Restaurant rejected the order');
      fetchAllOrders();
      fetchStats();
    } catch (error) {
      console.error('Failed to reject order:', error);
      alert('Failed to reject order');
    }
  };

  const markReady = async (orderId) => {
    try {
      await restaurantAPI.updateOrderStatus(orderId, { status: 'ready_for_pickup' });
      fetchAllOrders();
      fetchStats();
    } catch (error) {
      console.error('Failed to mark order as ready:', error);
      alert('Failed to mark order as ready');
    }
  };

  // Filter orders based on active tab
  const getFilteredOrders = () => {
    switch (activeOrderTab) {
      case 'pending':
        return allOrders.filter(o => o.status === 'pending');
      case 'active':
        return allOrders.filter(o => ['restaurant_confirmed', 'preparing', 'ready_for_pickup', 'assigned_delivery', 'picked_up', 'on_the_way'].includes(o.status));
      case 'completed':
        return allOrders.filter(o => ['delivered', 'cancelled'].includes(o.status));
      default:
        return allOrders;
    }
  };

  // Count orders for each tab
  const pendingCount = allOrders.filter(o => o.status === 'pending').length;
  const activeCount = allOrders.filter(o => ['restaurant_confirmed', 'preparing', 'ready_for_pickup', 'assigned_delivery', 'picked_up', 'on_the_way'].includes(o.status)).length;
  const completedCount = allOrders.filter(o => ['delivered', 'cancelled'].includes(o.status)).length;

  const filteredOrders = getFilteredOrders();

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      pending: '#F59E0B',
      restaurant_confirmed: '#3B82F6',
      preparing: '#8B5CF6',
      ready_for_pickup: '#06B6D4',
      assigned_delivery: '#22C55E',
      picked_up: '#22C55E',
      on_the_way: '#22C55E',
      delivered: '#10B981',
      cancelled: '#EF4444'
    };
    return colors[status] || '#6B7280';
  };

  // Format order time
  const formatOrderTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format date time for print
  const formatDateTimeIST = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Print Order Function - Optimized for 4x6 inch thermal printer
  // Uses iframe-based printing to avoid popup blocker issues with auto-print
  // Format date for print
  const formatDateForPrint = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const printOrder = (order, e, isAutoAccept = false, isManualReprint = false) => {
    if (e) e.stopPropagation();

    // Adopt any admin-pushed print settings (once per admin update) before reading local.
    applyAdminPrintSettings(authRestaurant);

    // Load printer settings from localStorage
    const defaultSettings = { paperWidth: '58', paperHeight: '150', fontSize: '9', fontWeight: 'bold', lineHeight: '1.15' };
    let printerSettings = defaultSettings;
    try {
      const saved = localStorage.getItem('printerSettings');
      if (saved) printerSettings = { ...defaultSettings, ...JSON.parse(saved) };
    } catch (e) { console.error('Failed to load printer settings:', e); }

    // Check if printing is disabled — manual reprint always allowed
    if (printerSettings.printEnabled === 'false' && !isManualReprint) {
      console.log('🖨️ Printing disabled — skipping auto receipt print');
      return;
    }

    // #9: dedup auto-prints across Dashboard+Orders and across reconnects/retried
    // webhooks. Manual reprints bypass. claimAutoPrint atomically marks the order.
    if (!isManualReprint) {
      if (!claimAutoPrint(order?._id || order?.id)) {
        console.log('🖨️ Skipping duplicate auto-print for order', order?.orderNumber);
        return;
      }
    }

    // Get restaurant details from state (restaurant from fetchRestaurant)
    // Note: order.restaurantId is just an ObjectId, not populated data
    const restaurantData = restaurant || {};
    const restaurantName = restaurantData.name || 'Restaurant';
    const restaurantAddress = restaurantData.address?.street || restaurantData.address?.fullAddress || '';
    const restaurantCity = restaurantData.address?.city || '';
    const restaurantState = restaurantData.address?.state || '';
    const restaurantPincode = restaurantData.address?.zipCode || '';
    // GST and FSSAI are nested under documents
    const restaurantGST = restaurantData.documents?.gst?.number || '';
    const restaurantPhone = restaurantData.contactPhone || restaurantData.phone || '';
    const restaurantFSSAI = restaurantData.documents?.fssai?.number || '';
    const isPaid = order.paymentMethod !== 'cash';

    // Calculate totals - ensure all values are valid numbers
    const subtotal = Number(order.pricing?.subtotal) || 0;
    // Under Section 9(5), restaurantEarnings.gstCollected is 0 (ECO pays GST)
    // Use pricing.tax (actual GST collected from customer) or platformEarnings.section9_5GstCollected as fallback
    const gstCollected = Number(order.earnings?.restaurantEarnings?.gstCollected) ||
                         Number(order.pricing?.tax) ||
                         Number(order.earnings?.platformEarnings?.section9_5GstCollected) || 0;
    const gstRate = Number(order.earnings?.restaurantEarnings?.gstRate) || 5;
    const cgstRate = gstRate / 2;
    const sgstRate = gstRate / 2;
    const cgstAmount = gstCollected / 2;
    const sgstAmount = gstCollected / 2;
    // Grand total = what customer pays for food (subtotal + GST - discount)
    // NOT restaurantEarnings.netPayable (that's restaurant's take after commission/TDS)
    const discount = Number(order.pricing?.discount) || 0;
    const grandTotal = subtotal + gstCollected - discount;
    const roundedTotal = Math.round(grandTotal);
    const roundOff = roundedTotal - grandTotal;
    const totalQty = (order.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);

    // Format date
    const formattedDate = order.createdAt ? formatDateForPrint(order.createdAt) : formatDateForPrint(new Date());

    // Build delivery address - Order model has: street, city, state, zipCode
    const delAddr = order.deliveryAddress ?
      [order.deliveryAddress.street, order.deliveryAddress.city, order.deliveryAddress.state, order.deliveryAddress.zipCode]
        .filter(Boolean).join(', ') || order.deliveryAddress.fullAddress || '' : '';

    // Build full receipt - FIXED character width for identical output on ALL platforms.
    // Courier New: 0.6 × fontSize — same in Chromium on macOS/Windows/Linux.
    const _paperPx = printerSettings.paperWidth === '80' ? 576 : 384;
    const _ml = parseInt(printerSettings.marginLeft || '2') || 2;
    const _mr = parseInt(printerSettings.marginRight || '2') || 2;
    const _charWidth = parseInt(printerSettings.fontSize || '9') * 0.6;
    const W = Math.max(20, Math.floor((_paperPx - _ml - _mr) / _charWidth));
    const line = '='.repeat(W);
    const dash = '-'.repeat(W);
    const pad = (t) => { const s = String(t); return ' '.repeat(Math.max(0, Math.floor((W - s.length) / 2))) + s; };
    // Helper: right-align a value on a line (label left, value flush right)
    // If label+value doesn't fit, put value on next line right-aligned
    const rAlign = (label, val) => {
      const v = String(val);
      const l = String(label);
      if (l.length + v.length + 1 > W) {
        // Doesn't fit - wrap label, value on next line right-aligned
        return l + '\n' + v.padStart(W);
      }
      const gap = W - l.length - v.length;
      return l + ' '.repeat(gap) + v;
    };
    // Helper: wrap a line to fit within W chars, indenting continuation lines
    const wrapLine = (text, indent = 2) => {
      if (text.length <= W) return text + '\n';
      let out = text.substring(0, W) + '\n';
      let rem = text.substring(W);
      const contW = W - indent;
      while (rem.length > 0) {
        out += ' '.repeat(indent) + rem.substring(0, contW) + '\n';
        rem = rem.substring(contW);
      }
      return out;
    };
    // Helper: wrap text and center each line (for header/footer)
    const padWrap = (text) => {
      if (text.length <= W) return pad(text) + '\n';
      let out = '';
      let rem = text;
      while (rem.length > 0) {
        out += pad(rem.substring(0, W)) + '\n';
        rem = rem.substring(W);
      }
      return out;
    };

    // Build items text - adapt column widths to paper size
    // 58mm: name(14) + qty(3) + price(7) + amt(8) = 32
    // 80mm: name(18) + qty(3) + price(9) + amt(12) = 42
    const is58 = W <= 32;
    const colQty = 3;
    const colPrice = is58 ? 7 : 9;
    const colAmt = is58 ? 8 : 12;
    const nameW = W - colQty - colPrice - colAmt;
    let itemsText = '';
    (order.items || []).forEach(item => {
      const qty = Number(item.quantity) || 1;
      const price = Number(item.price) || 0;
      const amt = qty * price;
      const fullName = item.name || 'Item';
      const numsPart = String(qty).padStart(colQty) + String(price.toFixed(0)).padStart(colPrice) + String(amt.toFixed(0)).padStart(colAmt);
      if (fullName.length <= nameW) {
        // Name fits in column - single line
        itemsText += fullName.padEnd(nameW) + numsPart + '\n';
      } else {
        // Name too long - full name on first line, numbers on next line right-aligned
        itemsText += fullName.substring(0, W) + '\n';
        itemsText += ''.padEnd(nameW) + numsPart + '\n';
      }
      if (item.variant) itemsText += '  ' + item.variant.substring(0, W - 2) + '\n';
      if (item.addons && item.addons.length > 0) {
        itemsText += '  +' + item.addons.map(a => a.name || '').join(',').substring(0, W - 3) + '\n';
      }
    });

    // Build GST text (amounts right-aligned)
    let gstText = '';
    if (gstCollected > 0) {
      gstText = rAlign(subtotal.toFixed(2) + '@ ' + cgstRate + '%CGST', cgstAmount.toFixed(2)) + '\n' +
                rAlign(subtotal.toFixed(2) + '@ ' + sgstRate + '%SGST', sgstAmount.toFixed(2)) + '\n';
    }

    let r = '';
    r += '<div style="text-align:center;font-size:' + (parseInt(printerSettings.fontSize) + 4) + 'px;font-weight:bold;word-wrap:break-word;max-width:100%;margin:0;padding:0">' + restaurantName + '</div><pre>';
    if (isPaid) r += padWrap('PAID');
    if (restaurantAddress) r += padWrap('Add.:- ' + restaurantAddress);
    if (restaurantCity || restaurantPincode) r += padWrap(restaurantCity + '- ' + restaurantPincode);
    if (restaurantGST) r += padWrap('GST NO. ' + restaurantGST);
    if (restaurantPhone) r += padWrap('Ph. ' + restaurantPhone);
    r += line + '\n';
    r += padWrap('INVOICE');
    r += line + '\n';
    r += wrapLine('From Foodwinc[' + order.orderNumber + ']');
    r += wrapLine('Name: ' + (order.userId?.name || 'Customer'));
    if (delAddr) r += wrapLine('Adr: ' + delAddr);
    r += dash + '\n';
    r += wrapLine(rAlign('Date: ' + formattedDate, 'Delivery'));
    r += wrapLine((isAutoAccept ? 'Cashier: Autoaccept  ' : '') + 'Bill No.: ' + order.orderNumber);
    r += dash + '\n';
    r += 'Item'.padEnd(nameW) + 'Qty'.padStart(colQty) + 'Price'.padStart(colPrice) + 'Amount'.padStart(colAmt) + '\n';
    r += dash + '\n';
    r += itemsText;
    r += dash + '\n';
    r += rAlign('Total Qty: ' + totalQty, 'Sub Total ' + subtotal.toFixed(2)) + '\n';
    r += gstText;
    if (discount > 0) r += rAlign('Discount', '-' + discount.toFixed(2)) + '\n';
    if (Math.abs(roundOff) >= 0.01) r += rAlign('Round off', roundOff.toFixed(2)) + '\n';
    r += line + '\n';
    r += '<b>' + rAlign('Grand Total', 'Rs.' + roundedTotal.toFixed(0)) + '</b>\n';
    r += line + '\n';
    r += wrapLine('Paid via ' + (order.paymentMethod === 'cash' ? 'Cash on Delivery' : 'Online [Foodwinc]'));
    if (order.specialInstructions) { r += dash + '\n'; r += wrapLine('Customer Notes: ' + order.specialInstructions); }
    r += dash + '\n';
    // Section 9(5) Notice - ECO is deemed supplier
    r += wrapLine('Tax to be paid under section 9(5) by Eco');
    r += dash + '\n';
    if (restaurantFSSAI) r += padWrap('FSSAI Lic No. ' + restaurantFSSAI);
    r += padWrap('NO EXCHANGE NO RETURN.');
    r += padWrap('--Thank You, Visit Again-- :)');

    // HTML for thermal printer - uses settings from localStorage
    const pw = printerSettings.paperWidth;
    const ph = printerSettings.paperHeight;
    const fs = printerSettings.fontSize;
    const fw = printerSettings.fontWeight;
    const lh = printerSettings.lineHeight;
    const bodyWidth = parseInt(pw) - 4; // subtract padding
    let _selPrinter = '';
    try { _selPrinter = localStorage.getItem('selectedPrinter') || ''; } catch (_) {}

    const _printerType = printerSettings.printerType || 'escpos';
    const _printDir = printerSettings.printDirection || '1';
    const _printDensity = printerSettings.printDensity || '8';
    const _printSpeed = printerSettings.printSpeed || '4';
    const ml = printerSettings.marginLeft || '2';
    const mr = printerSettings.marginRight || '2';
    const printContent = '<!DOCTYPE html><html data-printer="' + _selPrinter + '" data-paper="' + pw + '" data-printer-type="' + _printerType + '" data-direction="' + _printDir + '" data-density="' + _printDensity + '" data-speed="' + _printSpeed + '" data-margin-bottom="' + (printerSettings.marginBottom || '0') + '" data-raster-band="' + (printerSettings.rasterBandHeight || '0') + '"><head><meta charset="utf-8"><title>Invoice</title>' +
      '<style>' +
      '@page{margin:0!important;padding:0!important}' +
      'html{margin:0!important;padding:0!important}' +
      '*{box-sizing:border-box;page-break-inside:avoid!important;break-inside:avoid!important;-webkit-print-color-adjust:exact!important}' +
      'body{margin:0!important;padding:0 ' + mr + 'px 0 ' + ml + 'px;font-family:ReceiptMono,"Courier New",Courier,monospace;font-size:' + fs + 'px;font-weight:' + fw + ';line-height:' + lh + ';width:100%}' +
      'pre{margin:0;padding:0;white-space:pre-wrap;word-wrap:break-word}' +
      '</style></head><body>' + r + '</pre></body></html>';

    // Electron print — try server-generated image first (identical on ALL devices),
    // fall back to local HTML rendering if API fails (offline mode).
    if (window.electronAPI?.printReceiptImage && order._id) {
      const apiBase = window.location.origin.replace(':3001', ':3000').replace(':3002', ':3000');
      const qs = `paperWidth=${pw}&fontSize=${fs}&fontWeight=${fw}&lineHeight=${lh}&marginLeft=${ml}&marginRight=${mr}`;
      const imageUrl = `${apiBase}/api/orders/${order._id}/receipt-image?${qs}`;
      const token = localStorage.getItem('token') || '';
      console.log('🖨️ Dashboard: Printing via server image →', _selPrinter || 'default');
      const printSettings = {
        printerName: _selPrinter, printerType: _printerType, paperWidth: pw,
        printDirection: _printDir, printDensity: _printDensity, printSpeed: _printSpeed,
        marginBottom: printerSettings.marginBottom || '0',
      };
      // "Print only KOT" mode — skip the customer receipt, print JUST the KOT
      // (same server-image primitive). kotOnly wins over kotEnabled.
      if (printerSettings.kotOnly === 'true') {
        const kotOnlyUrl = `${apiBase}/api/orders/${order._id}/kot-image?${qs}`;
        console.log('🖨️ Dashboard: KOT-only mode → printing kitchen ticket, skipping receipt');
        window.electronAPI.printReceiptImage(kotOnlyUrl, printSettings, token)
          .then(result => {
            if (!result.success && result.stage !== 'print') {
              console.warn('🖨️ Dashboard: KOT-only print issue:', result.error);
              addToast('KOT sent to printer. If nothing printed, check the printer.', TOAST_TYPES.INFO);
            }
          })
          .catch(() => addToast('Print failed. Please try again.', TOAST_TYPES.ERROR));
        return;
      }
      window.electronAPI.printReceiptImage(imageUrl, printSettings, token)
        .then(result => {
          if (result.success) {
            console.log('🖨️ Dashboard: Receipt printed via server image');
          } else {
            console.warn('🖨️ Dashboard: Server image failed, falling back:', result.error);
            window.electronAPI.silentPrint(printContent).then(r2 => {
              if (!r2.success) addToast('Print failed: ' + r2.error, TOAST_TYPES.ERROR);
            }).catch(() => addToast('Print failed.', TOAST_TYPES.ERROR));
          }
          // Print KOT slip if enabled
          if (printerSettings.kotEnabled !== 'false' && order._id) {
            const kotQs = `paperWidth=${pw}&fontSize=${fs}&fontWeight=${fw}&lineHeight=${lh}&marginLeft=${ml}&marginRight=${mr}`;
            const kotUrl = `${apiBase}/api/orders/${order._id}/kot-image?${kotQs}`;
            setTimeout(() => {
              window.electronAPI.printReceiptImage(kotUrl, printSettings, token)
                .then(r => { if (!r.success) console.warn('🖨️ KOT print failed:', r.error); })
                .catch(() => {});
            }, 1000); // 1s delay — must exceed 0.5s print debounce timer
          }
        })
        .catch(() => {
          window.electronAPI.silentPrint(printContent).catch(() => {});
        });
      return;
    }
    if (window.electronAPI?.silentPrint) {
      console.log('🖨️ Dashboard: Printing via local HTML →', _selPrinter || 'default');
      window.electronAPI.silentPrint(printContent)
        .then(result => {
          if (!result.success) addToast('Print failed: ' + result.error, TOAST_TYPES.ERROR);
        })
        .catch(() => addToast('Print failed.', TOAST_TYPES.ERROR));
      return;
    }

    // Fallback: iframe-based printing for browser
    console.log('🖨️ Dashboard: Using iframe print (browser mode)...');
    try {
      // Remove any existing print iframe
      const existingIframe = document.getElementById('print-iframe-dashboard');
      if (existingIframe) {
        existingIframe.remove();
      }

      // Create hidden iframe for printing
      const iframe = document.createElement('iframe');
      iframe.id = 'print-iframe-dashboard';
      iframe.style.position = 'absolute';
      iframe.style.width = '58mm';
      iframe.style.height = '150mm';
      iframe.style.border = 'none';
      iframe.style.left = '-9999px';
      document.body.appendChild(iframe);

      // Write content to iframe
      const iframeDoc = iframe.contentWindow || iframe.contentDocument;
      const doc = iframeDoc.document || iframeDoc;
      doc.open();
      doc.write(printContent);
      doc.close();

      // Wait for content to fully render then print (Chrome/Chromium timing fix)
      const doPrint = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          console.log('🖨️ Dashboard: Print dialog opened successfully');

          // Clean up iframe after a delay (give time for print dialog)
          setTimeout(() => {
            if (iframe.parentNode) {
              iframe.parentNode.removeChild(iframe);
            }
          }, 2000);
        } catch (printError) {
          console.error('🖨️ Dashboard: Print failed:', printError);
          addToast('Failed to print order. Please try again.', TOAST_TYPES.ERROR);
          if (iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
        }
      };

      // Critical: Add delay to ensure content is rendered before print
      // Chrome/Chromium fires onload before content is visually ready
      iframe.onload = () => {
        setTimeout(doPrint, 500);
      };

      // Also trigger after a delay if onload doesn't fire
      setTimeout(() => {
        if (iframe.parentNode) {
          doPrint();
        }
      }, 800);
    } catch (error) {
      console.error('🖨️ Dashboard: Print setup failed:', error);
      addToast('Failed to print order. Please try again.', TOAST_TYPES.ERROR);

      // Fallback: try window.open method (works when triggered by user click)
      try {
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(printContent);
          printWindow.document.close();
          printWindow.onload = function() {
            printWindow.print();
          };
        } else {
          console.error('🖨️ Dashboard: Popup blocked - cannot print');
          addToast('Print blocked. Please click "Print Order" button manually.', TOAST_TYPES.WARNING);
        }
      } catch (fallbackError) {
        console.error('🖨️ Dashboard: Fallback print also failed:', fallbackError);
      }
    }
  };

  // FRONT-M1: sync printOrderRef in an effect, not during render. Render-phase
  // ref mutation is a side effect that breaks under React 18 concurrent /
  // StrictMode double-invocation.
  useEffect(() => { printOrderRef.current = printOrder; });

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: theme.backgroundColor
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔄</div>
          <p style={{ color: theme.textSecondary }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div style={{ background: theme.backgroundColor, minHeight: '100vh', padding: '16px' }}>
        <div style={{
          textAlign: 'center',
          padding: '48px 24px',
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{ fontSize: '100px', marginBottom: '24px' }}>🏪</div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: theme.textPrimary, marginBottom: '12px' }}>
            Welcome to Foodwinc
          </h2>
          <p style={{ fontSize: '16px', color: theme.textSecondary, marginBottom: '24px' }}>
            Register your restaurant to start receiving orders
          </p>
          <Link
            to="/restaurant/register"
            style={{
              display: 'inline-block',
              background: `linear-gradient(135deg, ${theme.primaryColor} 0%, ${theme.primaryDark} 100%)`,
              color: 'white',
              padding: '14px 32px',
              fontSize: '16px',
              fontWeight: '600',
              borderRadius: theme.radiusMedium,
              textDecoration: 'none'
            }}
          >
            Register Restaurant
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: theme.backgroundColor, minHeight: '100vh' }}>
      {/* Toast Notifications */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        maxWidth: '400px'
      }}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            onClick={() => removeToast(toast.id)}
            style={{
              padding: '14px 20px',
              borderRadius: '12px',
              background: toast.type === TOAST_TYPES.SUCCESS ? '#22C55E' :
                         toast.type === TOAST_TYPES.WARNING ? '#F59E0B' :
                         toast.type === TOAST_TYPES.ERROR ? '#EF4444' : '#3B82F6',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              cursor: 'pointer',
              animation: 'slideIn 0.3s ease-out',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <span>
              {toast.type === TOAST_TYPES.SUCCESS ? '✓' :
               toast.type === TOAST_TYPES.WARNING ? '⚠' :
               toast.type === TOAST_TYPES.ERROR ? '✕' : 'ℹ'}
            </span>
            {toast.message}
          </div>
        ))}
      </div>

      <div style={{ padding: '24px' }}>

        {/* Restaurant Header - Flutter style */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '16px'
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: theme.radiusMedium,
            background: `linear-gradient(135deg, ${theme.primaryColor} 0%, ${theme.primaryDark} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <span style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
              {restaurant?.name ? restaurant.name.substring(0, 1).toUpperCase() : 'R'}
            </span>
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: theme.textPrimary,
              margin: 0
            }}>
              {restaurant?.name || 'Restaurant'}
            </h1>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              marginTop: '4px'
            }}>
              <span style={{ color: theme.warningColor, fontSize: '14px' }}>★</span>
              <span style={{ fontSize: '14px', color: theme.textPrimary }}>
                {restaurant?.rating?.toFixed(1) || '0.0'}
              </span>
              <span style={{ fontSize: '12px', color: theme.textSecondary, marginLeft: '4px' }}>
                ({restaurant?.totalReviews || 0} reviews)
              </span>
            </div>
          </div>
        </div>

        {/* Subscription Status Banner - Flutter style */}
        {/* Subscription/trial card removed. Keep a clean Earnings & Payouts link. */}
        <Link
          to="/restaurant/earnings"
          style={{
            display: 'block', width: '100%', maxWidth: '400px', padding: '14px',
            background: `linear-gradient(135deg, ${theme.primaryColor} 0%, ${theme.primaryDark} 100%)`,
            color: 'white', borderRadius: '8px', textAlign: 'center', textDecoration: 'none',
            fontSize: '15px', fontWeight: '600', boxSizing: 'border-box', marginBottom: '16px'
          }}
        >
          💰 Earnings & Payouts
        </Link>

        {/* Tomato payout calculator — see real take-home vs FoodWinc */}
        <Link
          to="/restaurant/tomato-calculator"
          style={{
            display: 'block', width: '100%', maxWidth: '400px', padding: '14px',
            background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
            color: 'white', borderRadius: '8px', textAlign: 'center',
            textDecoration: 'none', fontSize: '15px', fontWeight: 600,
            boxSizing: 'border-box', marginBottom: '16px',
          }}
        >
          🧮 Are you making money on Tomato? — real profit calculator
        </Link>

        {/* Store Status Toggle - Flutter slider style */}
        <div
          ref={sliderRef}
          style={{
            padding: '16px',
            background: isOpen
              ? `rgba(34, 197, 94, 0.1)`
              : `rgba(239, 68, 68, 0.1)`,
            borderRadius: theme.radiusMedium,
            border: `1px solid ${isOpen ? theme.successColor : theme.errorColor}`,
            marginBottom: '16px',
            maxWidth: '400px'
          }}
        >
          <div
            style={{
              height: '56px',
              borderRadius: '28px',
              background: isOpen
                ? `linear-gradient(90deg, ${theme.successColor} 0%, rgba(34, 197, 94, 0.7) 100%)`
                : `linear-gradient(90deg, rgba(239, 68, 68, 0.7) 0%, ${theme.errorColor} 100%)`,
              position: 'relative',
              cursor: 'pointer',
              overflow: 'hidden'
            }}
            onMouseMove={handleSliderDrag}
            onMouseUp={handleSliderDragEnd}
            onMouseLeave={handleSliderDragEnd}
            onTouchMove={handleSliderDrag}
            onTouchEnd={handleSliderDragEnd}
          >
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {isOpen ? (
                <>Slide to Close <span>→</span></>
              ) : (
                <><span>←</span> Slide to Open</>
              )}
            </div>

            <div
              style={{
                position: 'absolute',
                top: '4px',
                left: isDragging
                  ? `${Math.max(4, Math.min(sliderPosition * (sliderRef.current?.offsetWidth - 74) || 0, (sliderRef.current?.offsetWidth - 74) || 0))}px`
                  : isOpen ? '4px' : `calc(100% - 74px)`,
                width: '70px',
                height: '48px',
                borderRadius: '24px',
                background: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 3px 10px rgba(0, 0, 0, 0.25)',
                transition: isDragging ? 'none' : 'left 0.3s ease',
                cursor: 'grab'
              }}
              onMouseDown={handleSliderDragStart}
              onTouchStart={handleSliderDragStart}
            >
              {isTogglingStatus ? (
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: `2px solid ${isOpen ? theme.successColor : theme.errorColor}`,
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill={isOpen ? theme.successColor : theme.errorColor}>
                  {isOpen ? (
                    <path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h1.9c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm0 12H6V10h12v10z"/>
                  ) : (
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z"/>
                  )}
                </svg>
              )}
            </div>
          </div>
          <p style={{
            fontSize: '14px',
            color: theme.textSecondary,
            textAlign: 'center',
            marginTop: '12px',
            marginBottom: scheduleMessage ? '8px' : 0
          }}>
            {isOpen ? 'Accepting orders from customers' : 'Not accepting new orders'}
          </p>
          {/* Schedule message (matching Flutter app) */}
          {scheduleMessage && (
            <p style={{
              fontSize: '12px',
              color: isClosedBySchedule ? theme.warningColor : theme.successColor,
              textAlign: 'center',
              marginTop: 0,
              marginBottom: 0,
              fontWeight: '500'
            }}>
              {isClosedBySchedule ? '⏰ ' : '✓ '}{scheduleMessage}
            </p>
          )}
        </div>

        {/* Today's Overview - Flutter style */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{
            fontSize: '16px',
            fontWeight: '700',
            color: theme.textPrimary,
            marginBottom: '12px'
          }}>
            Today's Overview
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <StatCard
              title="Orders"
              value={stats?.todayOrders || 0}
              icon="📦"
              color={theme.primaryColor}
            />
            <StatCard
              title="Revenue"
              value={`₹${(stats?.todayRevenue || 0).toLocaleString()}`}
              icon="₹"
              color={theme.successColor}
            />
            <StatCard
              title="Active"
              value={stats?.activeOrders || 0}
              icon="🔥"
              color={theme.warningColor}
            />
            <StatCard
              title="Delivered"
              value={stats?.todayDelivered || 0}
              icon="✓"
              color={theme.infoColor}
            />
          </div>
        </div>

        {/* Pricing edge nudge — turn the commission you save into a compete-discount */}
        {pricingEdge && (
          <div style={{
            marginBottom: '24px',
            background: 'linear-gradient(135deg, #ECFDF5, #EFF6FF)',
            border: '1px solid #A7F3D0',
            borderRadius: theme.radiusMedium,
            padding: '16px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#065F46', margin: 0 }}>💡 Your pricing edge</h2>
              <span style={{ fontSize: '12px', color: theme.textSecondary }}>
                You keep <b style={{ color: theme.successColor }}>{pricingEdge.ourKeepPct}%</b> here vs ~<b style={{ color: theme.errorColor }}>{pricingEdge.competitorKeepPct}%</b> on other apps
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
              <div style={{ background: '#fff', borderRadius: '10px', padding: '12px 14px', border: '1px solid #D1FAE5' }}>
                <div style={{ fontSize: '12px', color: theme.textSecondary }}>Safe discount headroom</div>
                <div style={{ fontSize: '26px', fontWeight: '800', color: theme.successColor }}>~{pricingEdge.headroomPct}% off</div>
                <div style={{ fontSize: '12px', color: theme.textHint }}>and still take home the same as a full-price order elsewhere</div>
              </div>
              <div style={{ background: '#fff', borderRadius: '10px', padding: '12px 14px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ fontSize: '13px', color: theme.textPrimary, lineHeight: 1.5 }}>
                  On other apps you lose <b>~{pricingEdge.competitorTakePct}%</b> to commission &amp; fees (up to ~{pricingEdge.competitorTakeWithAdsPct}% with ads). Here it's just <b>{pricingEdge.ourTakePct}%</b>. That saved commission is your discount budget — use it to beat their price and keep more.
                  {pricingEdge.markupPercent ? (
                    <div style={{ marginTop: '6px', color: '#B45309' }}>
                      Your prices on other apps are ~<b>{pricingEdge.markupPercent}%</b> higher{pricingEdge.markupSample ? ` (${pricingEdge.markupSample} items)` : ''} — customers already pay more there.
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/restaurant/coupons')}
                style={{
                  background: theme.successColor, color: '#fff', border: 'none', borderRadius: '8px',
                  padding: '10px 16px', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
                }}
              >
                🎟️ Create a discount
              </button>
              <span style={{ fontSize: '12px', color: theme.textHint }}>Customers see your real, un-inflated prices — a small offer here wins the order.</span>
            </div>
          </div>
        )}

        {/* Order Management Tabs - Flutter style */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{
            fontSize: '16px',
            fontWeight: '700',
            color: theme.textPrimary,
            marginBottom: '12px'
          }}>
            Order Management
          </h2>

          {/* Tab Navigation */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '16px',
            background: 'white',
            padding: '8px',
            borderRadius: theme.radiusMedium,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            {[
              { id: 'pending', label: 'New', count: pendingCount, color: theme.warningColor },
              { id: 'active', label: 'Active', count: activeCount, color: theme.infoColor },
              { id: 'completed', label: 'Completed', count: completedCount, color: theme.successColor }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveOrderTab(tab.id)}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  background: activeOrderTab === tab.id ? theme.primaryColor : 'white',
                  color: activeOrderTab === tab.id ? 'white' : theme.textSecondary,
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span style={{
                    background: activeOrderTab === tab.id ? 'rgba(255,255,255,0.25)' : tab.color,
                    color: activeOrderTab === tab.id ? 'white' : 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '700',
                    minWidth: '24px',
                    textAlign: 'center'
                  }}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Order List */}
          {filteredOrders.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              background: 'white',
              borderRadius: theme.radiusMedium,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.3 }}>
                {activeOrderTab === 'pending' ? '📋' : activeOrderTab === 'active' ? '👨‍🍳' : '✅'}
              </div>
              <p style={{ fontSize: '14px', color: theme.textSecondary, margin: 0 }}>
                {activeOrderTab === 'pending' ? 'No new orders' :
                 activeOrderTab === 'active' ? 'No active orders' :
                 'No completed orders'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '16px' }}>
              {filteredOrders.slice(0, 5).map((order) => (
                <div key={order._id} style={{
                  position: 'relative',
                  overflow: 'hidden',
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  border: '1px solid #E2E8F0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'}>
                  {/* Status Badge - Absolute positioned */}
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    background: getStatusColor(order.status),
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {order.status.replace(/_/g, ' ')}
                  </div>

                  {/* Order Header */}
                  <div style={{ paddingRight: '140px', marginBottom: '16px' }}>
                    <h2 style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#1E293B',
                      marginBottom: '4px',
                      margin: 0
                    }}>
                      Order #{order.orderNumber}
                    </h2>
                    <p style={{
                      fontSize: '13px',
                      color: '#64748B',
                      margin: 0
                    }}>
                      {formatOrderTime(order.createdAt)}
                    </p>
                  </div>

                  {/* Customer Info */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px',
                    paddingBottom: '16px',
                    borderBottom: '1px solid #F1F5F9'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: '700',
                      fontSize: '16px'
                    }}>
                      {(order.userId?.name || 'U')[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1E293B',
                        margin: 0,
                        marginBottom: '2px'
                      }}>
                        {order.userId?.name || 'Unknown User'}
                      </p>
                      <p style={{
                        fontSize: '13px',
                        color: '#64748B',
                        margin: 0
                      }}>
                        {order.userId?.phone || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Delivery Partner Info */}
                  {order.deliveryPartnerId && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '16px',
                      paddingBottom: '16px',
                      borderBottom: '1px solid #F1F5F9'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #22C55E, #16A34A)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '16px'
                      }}>
                        {(order.deliveryPartnerId?.name || 'D')[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#1E293B',
                          margin: 0,
                          marginBottom: '2px'
                        }}>
                          🚴 {order.deliveryPartnerId?.name || 'Delivery Partner'}
                        </p>
                        <p style={{
                          fontSize: '13px',
                          color: '#64748B',
                          margin: 0
                        }}>
                          📞 {order.deliveryPartnerId?.phone || 'N/A'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Special Instructions — shown on the card so it's seen at a glance */}
                  {order.specialInstructions && (
                    <div style={{ marginBottom: '16px', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '8px', padding: '10px 12px' }}>
                      <p style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: '#92400E' }}>📝 Special Instructions</p>
                      <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#92400E', whiteSpace: 'pre-wrap' }}>{order.specialInstructions}</p>
                    </div>
                  )}

                  {/* Order Items */}
                  <div style={{ marginBottom: '16px' }}>
                    {order.items?.map((item, index) => {
                      const qty = Number(item.quantity) || 1;
                      let label = item.name || item.menuItem?.name || 'Item';
                      if (item.variant) label += ` ${item.variant}`;
                      if (item.addons?.length > 0) {
                        const addons = item.addons
                          .map(a => (typeof a === 'string' ? a : (a && a.name) || ''))
                          .filter(Boolean);
                        if (addons.length) label += ` + ${addons.join(', ')}`;
                      }
                      const lineTotal = (item.subtotal != null && Number(item.subtotal) > 0)
                        ? Number(item.subtotal)
                        : (Number(item.price) || 0) * qty;
                      return (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: '12px',
                            padding: '8px 0'
                          }}
                        >
                          <span style={{ fontSize: '14px', color: '#1E293B', fontWeight: 600, lineHeight: 1.4 }}>
                            {qty} x {label}
                          </span>
                          <span style={{ fontSize: '14px', color: '#1E293B', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            ₹{lineTotal.toFixed(0)}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Total */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '16px',
                    marginBottom: '20px',
                    borderTop: '1px solid #F1F5F9'
                  }}>
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#64748B'
                    }}>
                      Total
                    </span>
                    <span style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#FF6B6B'
                    }}>
                      ₹{order.earnings?.restaurantEarnings?.netPayable > 0
                        ? order.earnings.restaurantEarnings.netPayable.toFixed(0)
                        : ((order.pricing?.subtotal || 0) + (order.earnings?.restaurantEarnings?.gstCollected || 0)).toFixed(0)}
                    </span>
                  </div>

                  {/* Action Buttons based on status */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {order.status === 'pending' && (
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); acceptOrder(order._id, order); }}
                          onMouseEnter={(e) => e.target.style.background = '#EF4444'}
                          onMouseLeave={(e) => e.target.style.background = '#FF6B6B'}
                          style={{
                            flex: 1,
                            padding: '14px',
                            border: 'none',
                            borderRadius: '8px',
                            background: '#FF6B6B',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          Accept
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); rejectOrder(order._id); }}
                          onMouseEnter={(e) => e.target.style.background = '#F8FAFC'}
                          onMouseLeave={(e) => e.target.style.background = 'white'}
                          style={{
                            padding: '14px 20px',
                            border: '1px solid #E2E8F0',
                            borderRadius: '8px',
                            background: 'white',
                            color: '#64748B',
                            fontWeight: '600',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          Reject
                        </button>
                      </div>
                    )}

                    {order.status === 'restaurant_confirmed' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); acceptOrder(order._id, order); }}
                        onMouseEnter={(e) => e.target.style.background = '#2563EB'}
                        onMouseLeave={(e) => e.target.style.background = '#3B82F6'}
                        style={{
                          width: '100%',
                          padding: '14px',
                          border: 'none',
                          borderRadius: '8px',
                          background: '#3B82F6',
                          color: 'white',
                          fontWeight: '600',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Start Preparing
                      </button>
                    )}

                    {order.status === 'preparing' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); markReady(order._id); }}
                        onMouseEnter={(e) => e.target.style.background = '#16A34A'}
                        onMouseLeave={(e) => e.target.style.background = '#22C55E'}
                        style={{
                          width: '100%',
                          padding: '14px',
                          border: 'none',
                          borderRadius: '8px',
                          background: '#22C55E',
                          color: 'white',
                          fontWeight: '600',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        📦 Mark as Ready
                      </button>
                    )}

                    {/* Print Order Button - Available for active orders */}
                    {['restaurant_confirmed', 'preparing', 'ready_for_pickup', 'assigned_delivery', 'picked_up', 'on_the_way'].includes(order.status) && (
                      <button
                        onClick={(e) => printOrder(order, e, false, true)}
                        onMouseEnter={(e) => e.target.style.background = '#F1F5F9'}
                        onMouseLeave={(e) => e.target.style.background = 'white'}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px',
                          background: 'white',
                          color: '#64748B',
                          fontWeight: '600',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        🖨️ Print Order
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {/* View All Link */}
              {filteredOrders.length > 5 && (
                <Link
                  to="/restaurant/orders"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    padding: '14px',
                    background: 'white',
                    borderRadius: theme.radiusMedium,
                    color: theme.primaryColor,
                    fontSize: '14px',
                    fontWeight: '600',
                    textDecoration: 'none',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}
                >
                  View all {filteredOrders.length} orders →
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Cannot Open Modal (matching Flutter app) */}
      {showCannotOpenModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '24px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: `rgba(245, 158, 11, 0.1)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: '32px'
            }}>
              ⏰
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: theme.textPrimary,
              marginBottom: '8px'
            }}>
              Cannot Open Restaurant
            </h3>
            <p style={{
              fontSize: '14px',
              color: theme.textSecondary,
              marginBottom: '8px'
            }}>
              Your restaurant is currently outside business hours.
            </p>
            {scheduleMessage && (
              <p style={{
                fontSize: '16px',
                color: theme.warningColor,
                fontWeight: '600',
                marginBottom: '24px'
              }}>
                {scheduleMessage}
              </p>
            )}
            <p style={{
              fontSize: '13px',
              color: theme.textHint,
              marginBottom: '24px'
            }}>
              You can update your opening hours in restaurant settings to change when your restaurant can accept orders.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowCannotOpenModal(false)}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: theme.textSecondary,
                  color: 'white',
                  border: 'none',
                  borderRadius: theme.radiusMedium,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                OK
              </button>
              <button
                onClick={() => {
                  setShowCannotOpenModal(false);
                  navigate('/restaurant/settings');
                }}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: `linear-gradient(135deg, ${theme.primaryColor} 0%, ${theme.primaryDark} 100%)`,
                  color: 'white',
                  border: 'none',
                  borderRadius: theme.radiusMedium,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Update Hours
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// Stat Card Component - Flutter style
function StatCard({ title, value, icon, color }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: theme.radiusMedium,
      padding: '16px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    }}>
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        background: `${color}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '12px',
        fontSize: '16px',
        color: color
      }}>
        {icon}
      </div>
      <div style={{ fontSize: '20px', fontWeight: '700', color: theme.textPrimary, marginBottom: '4px' }}>
        {value}
      </div>
      <div style={{ fontSize: '12px', color: theme.textSecondary }}>
        {title}
      </div>
    </div>
  );
}

export default RestaurantDashboard;
